/* eslint-disable react/require-default-props */
import React from 'react';
import { useDisclosure, useToast } from '@chakra-ui/core';
import pick from 'lodash/pick';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { TodosType } from '../../firebase/firestore/documents/lookups';
import { RecruiterActionFormModal } from '../dashboard/RecruiterActionFormModal';
import { InReviewQuestionListType } from '../../firebase/firestore/documents/candidate';
import { useUserPermission } from '../../services/permission/useUserPermission';
import { EmployeePermissionsEnum } from '../administration/team/teamPermission/teamPermission.constant';
import { POSITION_CANDIDATE_TABS } from '../../routes/constants';
import { SeekerContainerTabItems } from '../messaging/seeker/SeekerContainerContext';
import { LanguageEvaluationModal } from '../dashboard/LanguageEvaluationModal';
import { SendHRformModal } from '../dashboard/SendHRformModal';
import { HireRejectCandidateModal } from '../messaging/seeker/header/actionsDropdown/HireRejectCandidateModal';
import { ReviewPausedQuestionModal } from '../dashboard/ReviewPausedQuestionModal';
import { BackgroundCheckCompletedReviewModal } from '../dashboard/BackgroundCheckCompletedReviewModal';

interface ActionModalsProps {
  candidateTodos?: TodosType;
}

type SelectedTodo = {
  id: string;
  task: string;
  candidateName: string;
  candidateId: string;
  position: string;
  helpRequestedMessages?: {
    userType: string;
    message: string;
    timestamp: Date;
    name: string;
  }[];
  latestHelpRequestFor?: string;
  seekerHireDetailId?: string;
  hireDetailsRequestedAt?: string;
  latestHelpRequestedLabel?: Record<string, string>;
  hireFormTemplate?: {
    defaultLanguage: string;
    languages: string[];
  };
  hireFormTemplateId?: string;
  inReviewQuestionList?: InReviewQuestionListType;
  monetized?: boolean;
  isSeekerPreviouslyMonetized?: boolean;
};

enum ModalTypes {
  LE = 'languageEvaluation',
  WBA = 'workbrightAccepted',
  WBC = 'workbrightCompleted',
  RPQA = 'reviewPausedQuestionAnswer',
  HM = 'hireModal',
  HRM = 'hireRejectModal',
  UPIN = 'upcomingInterviews',
  BCC = 'bgCheckCleared',
  BCFC = 'bgCheckFlaggedByCertnCandidates',
  DEFAULT = 'default',
}

const offerRejectedTasks = [
  'offerRejected12HoursAgo',
  'offerRejected24HoursAgo',
  'offerRejected36HoursAgo',
  'offerRejected48HoursAgo',
  'offerExpired12HoursAgo',
  'offerExpired24HoursAgo',
  'offerExpired36HoursAgo',
  'offerExpired48HoursAgo',
];

const offerAcceptedTasks = [
  'offerAccepted12HoursAgo',
  'offerAccepted24HoursAgo',
  'offerAccepted36HoursAgo',
  'offerAccepted48HoursAgo',
];

export const ActionModals = React.memo(function ActionModals({ candidateTodos }: ActionModalsProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { checkPermission } = useUserPermission();
  const toast = useToast();
  const { t } = useTranslation('toDoList');
  const history = useHistory();

  const { selectedTodo, messagingViewModal } = useStoreState((store) => store.todo);
  const {
    setSelectedTodo,
    setMessagingViewModal,
    resetState,
    setSelectedCandidate,
    setOpenCandidateMessagingModal,
  } = useStoreActions((actions) => actions.todo);
  const { getWorkbrightLink } = useStoreActions((actions) => actions.candidate);
  const [modalType, setModalType] = React.useState<ModalTypes | null>(null);

  const selectedTodoItem = React.useMemo<SelectedTodo | undefined>(() => {
    if (!selectedTodo) return undefined;
    return {
      ...pick(selectedTodo, [
        'id',
        'candidateId',
        'position',
        'helpRequestedMessages',
        'seekerHireDetailId',
        'hireDetailsRequestedAt',
        'latestHelpRequestedLabel',
        'hireFormTemplate',
        'hireFormTemplateId',
        'monetized',
        'isSeekerPreviouslyMonetized',
      ]),
      task: selectedTodo.title,
      candidateName: selectedTodo.candidateFullName,
      latestHelpRequestFor: selectedTodo?.latestHelpRequestedFor,
      inReviewQuestionList: selectedTodo?.inReviewQuestionList as InReviewQuestionListType | undefined,
    };
  }, [selectedTodo]);

  const handleWorkbrightLink = React.useCallback(
    async (id: string) => {
      if (!id) return;
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await getWorkbrightLink({ candidateId: id });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (response?.link) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          window.open(response.link, '_blank');
        } else {
          console.error('No valid url found.');
        }
      } catch (e) {
        console.log('Error while getting the link', e);
      }
    },
    [getWorkbrightLink],
  );

  const onActionModalOpen = React.useCallback(
    (candidateId: string) => {
      setSelectedCandidate(candidateId);
      setMessagingViewModal(false);
      setOpenCandidateMessagingModal(true);
    },
    [setMessagingViewModal, setOpenCandidateMessagingModal, setSelectedCandidate],
  );

  React.useEffect(() => {
    const handleSelectedTodoChange = async () => {
      if (!selectedTodo) return;

      const { title: task, candidateId, position } = selectedTodo;

      let newModalType: ModalTypes | null = null;

      if (task === ModalTypes.LE) {
        newModalType = ModalTypes.LE;
      } else if (task === ModalTypes.WBA) {
        if (checkPermission({ permissions: [EmployeePermissionsEnum.I9_REVIEW] })) {
          newModalType = ModalTypes.WBA;
        } else {
          toast({ description: t('common:noPermission'), status: 'error', isClosable: true });
        }
      } else if (task === ModalTypes.WBC) {
        if (checkPermission({ permissions: [EmployeePermissionsEnum.I9_REVIEW] })) {
          await handleWorkbrightLink(candidateId);
        } else {
          toast({ description: t('common:noPermission'), status: 'error', isClosable: true });
        }
      } else if (task === ModalTypes.RPQA) {
        newModalType = ModalTypes.RPQA;
      } else if (offerRejectedTasks.includes(task)) {
        const seekerId = candidateId.split('_')[1];
        history.push({
          pathname: POSITION_CANDIDATE_TABS(position, SeekerContainerTabItems.Messaging),
          state: { seekerIdFromTopCandidate: seekerId },
        } as any);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        resetState();
      } else if (offerAcceptedTasks.includes(task)) {
        newModalType = ModalTypes.HRM;
      } else if (task === ModalTypes.UPIN) {
        onActionModalOpen(candidateId);
        return;
      } else if (task === ModalTypes.BCC) {
        newModalType = ModalTypes.BCC;
      } else if (task === ModalTypes.BCFC) {
        newModalType = ModalTypes.BCFC;
      } else {
        newModalType = ModalTypes.DEFAULT;
      }

      if (newModalType) {
        setModalType(newModalType);
        onOpen();
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleSelectedTodoChange();
  }, [selectedTodo, checkPermission, handleWorkbrightLink, history, onOpen, t, toast, resetState, onActionModalOpen]);

  if (!selectedTodo || !isOpen || !selectedTodoItem) return null;

  function handleClose() {
    setSelectedTodo(null);
    setMessagingViewModal(false);
    setSelectedCandidate(null);
    setOpenCandidateMessagingModal(false);
    setModalType(null);
    onClose();
  }

  if (candidateTodos && modalType === ModalTypes.DEFAULT) {
    return (
      <RecruiterActionFormModal
        selectedToDoItem={selectedTodoItem}
        candidateTodos={candidateTodos}
        isOpen={isOpen}
        onClose={handleClose}
        messagingModal={messagingViewModal}
      />
    );
  }

  if (modalType === ModalTypes.LE) {
    return (
      <LanguageEvaluationModal
        isOpen={isOpen}
        onClose={handleClose}
        currentCandidateId={selectedTodoItem.candidateId}
        currentTask={selectedTodoItem.task}
        currentDocId={selectedTodoItem.id}
        messagingModal={messagingViewModal}
        isRenderedBy="DashboardHome"
      />
    );
  }

  if (modalType === ModalTypes.HRM) {
    return (
      <SendHRformModal
        isOpen={isOpen}
        onClose={handleClose}
        currentCandidateId={selectedTodoItem.candidateId}
        currentTask={selectedTodoItem.task}
        currentDocId={selectedTodoItem.id}
      />
    );
  }

  if (modalType === ModalTypes.BCC || modalType === ModalTypes.BCFC) {
    return (
      <BackgroundCheckCompletedReviewModal
        isOpen={isOpen}
        onClose={handleClose}
        currentCandidateId={selectedTodoItem.candidateId}
        currentTask={selectedTodoItem.task}
        currentDocId={selectedTodoItem.id}
        selectedToDoStatus={selectedTodoItem.task}
      />
    );
  }

  if (modalType === ModalTypes.WBA) {
    return (
      <HireRejectCandidateModal
        isOpen={isOpen}
        onClose={handleClose}
        candidateIdFromProps={selectedTodoItem?.candidateId}
        selectedToDoItem={selectedTodoItem}
        isFromDashboard
      />
    );
  }

  if (modalType === ModalTypes.RPQA) {
    return <ReviewPausedQuestionModal isOpen={isOpen} onClose={handleClose} selectedToDoItem={selectedTodoItem} />;
  }

  return null;
});
